import type { DocumentLayoutV1, LayoutBand, LayoutElement, LayoutElementKey } from '../../utils/documentLayoutTypes';
import {
  DIN5008_FORM_B_ADDRESS_WINDOW,
  LAYOUT_LINE_MIN_THICKNESS_PT,
  PDF_A4_HEIGHT_PT,
  PDF_A4_WIDTH_PT,
  isLabeledFieldKey,
  newLayoutElementId,
  resolveLineOrientation,
} from '../../utils/documentLayoutTypes';
import type { CompanySettings } from '../../../../types';
import {
  formatCompanyAddressLines,
  formatCompanyCompactSenderLine,
  formatDirectorFooterLines,
  formatRegistryLine,
} from '../../../leads/utils/offerDocumentUtils';
import {
  defaultDocumentNumberFormatSettings,
  invoiceNumberBuildFields,
  invoicePrefixForLabel,
  type DocumentNumberFormatSettings,
} from '../../../../utils/documentNumberFormat';
import {
  documentNumberFieldLabel,
  expandFreeTextTokens,
  inlineLabelValueSeparator,
  issueDateFieldLabel,
  labeledFieldPresentation,
  leistungszeitraumFieldLabel,
  formatOfferTitleLine,
  resolveFieldLabel,
  resolveLabelPlacement,
  DEFAULT_FIELD_LABELS,
  type LayoutTokenValues,
} from '../../utils/layoutFieldPresentation';
import { canRemoveElement } from '../../utils/documentLayoutValidation';

export type LayoutSampleData = {
  companyName: string;
  companyHeadline: string;
  recipientName: string;
  recipientStreet: string;
  recipientCity: string;
  ansprechLine: string;
  ansprechMail: string;
  documentTitle: string;
  offerTitleRest: string;
  documentType: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  customerNumber: string;
  projectNumber: string;
  orderNumber: string;
  leistungsort: string;
  leistungszeitraum: string;
  iban: string;
  bic: string;
  bankName: string;
  phone: string;
  email: string;
  website: string;
  companyStreet: string;
  companyCity: string;
  vatId: string;
  taxNumber: string;
  directors: string;
  registry: string;
};

/** Placeholder serial like XXXXX matching the configured digit count. */
export function buildSampleInvoiceNumber(
  format: DocumentNumberFormatSettings = defaultDocumentNumberFormatSettings(),
  date: Date = new Date(),
): string {
  const prefix = invoicePrefixForLabel(format, 'Rechnung');
  const digits = Math.max(1, format.invoice_number_seq_digits);
  const xs = 'X'.repeat(digits);
  const fields = invoiceNumberBuildFields(format, date);
  const segments: string[] = [];
  if (prefix) segments.push(prefix);
  if (fields.yearMode !== 'none') segments.push(fields.yearKey);
  if (fields.includeMonth && fields.monthKey) segments.push(fields.monthKey);
  segments.push(xs);
  return segments.join(fields.delimiter ?? '-');
}

const DEFAULT_INVOICE_NUMBER = buildSampleInvoiceNumber();

export const LAYOUT_SAMPLE_DATA: LayoutSampleData = {
  companyName: 'HWS Haustechnik GmbH',
  companyHeadline: 'HWS Haustechnik GmbH | Musterstraße 1 | 12345 Musterstadt',
  recipientName: 'Musterkunde GmbH',
  recipientStreet: 'Beispielweg 12',
  recipientCity: '12345 Musterstadt',
  ansprechLine: 'Max Mustermann',
  ansprechMail: 'max@firma.de',
  documentTitle: `Rechnung ${DEFAULT_INVOICE_NUMBER}`,
  offerTitleRest: 'Heizlastberechnung Bürogebäude',
  documentType: 'Rechnung',
  invoiceNumber: DEFAULT_INVOICE_NUMBER,
  issueDate: '10.08.2026',
  dueDate: '24.08.2026',
  customerNumber: 'KND-26-XXXXX',
  projectNumber: 'P-26-XXXXX',
  orderNumber: 'PO-2026-0042',
  leistungsort: 'Beispielweg 12, 12345 Musterstadt',
  leistungszeitraum: '01.08.2026 – 08.08.2026',
  iban: 'DE50 1007 2324 0066 5265 00',
  bic: 'DEUTDEDBP32',
  bankName: 'Deutsche Bank',
  phone: '030 56978704',
  email: 'info@hws-haustechnik.de',
  website: 'www.hws-haustechnik.de',
  companyStreet: 'Musterstraße 1',
  companyCity: '12345 Musterstadt',
  vatId: 'DE123456789',
  taxNumber: '29/123/45678',
  directors: 'Geschäftsführer: Max Mustermann',
  registry: 'Amtsgericht Berlin HRB 123456',
};

/** Live preview data: Ansprechpartner = current user (same APIs as OfferDocumentEditor). */
export function buildLayoutSampleData(opts: {
  company: CompanySettings | null;
  userName: string;
  userEmail: string;
  documentNumberFormat?: DocumentNumberFormatSettings | null;
}): LayoutSampleData {
  const { company, userName, userEmail, documentNumberFormat } = opts;
  const base = { ...LAYOUT_SAMPLE_DATA };
  // Never keep HWS placeholders for company-backed fields when settings are empty.
  base.iban = '';
  base.bic = '';
  base.bankName = '';
  base.phone = '';
  base.email = '';
  base.website = '';
  base.companyStreet = '';
  base.companyCity = '';
  base.vatId = '';
  base.taxNumber = '';
  base.directors = '';
  base.registry = '';
  const name = userName.trim();
  const mail = userEmail.trim();
  const companyMail = (company?.email ?? '').trim();
  // Name + mail of the logged-in user (as on the document contact block).
  base.ansprechLine = name || base.ansprechLine;
  base.ansprechMail = mail || companyMail;

  // Always keep clear Musterdaten for recipient / Leistungsort in the template editor.
  base.recipientName = 'Musterkunde GmbH';
  base.recipientStreet = 'Beispielweg 12';
  base.recipientCity = '12345 Musterstadt';
  base.leistungsort = 'Beispielweg 12, 12345 Musterstadt';

  const format = documentNumberFormat ?? defaultDocumentNumberFormatSettings();
  const invoiceNumber = buildSampleInvoiceNumber(format);
  base.invoiceNumber = invoiceNumber;
  base.documentType = 'Rechnung';
  base.documentTitle = `Rechnung ${invoiceNumber}`;

  if (!company) {
    base.companyName = '';
    base.companyHeadline = '';
    return base;
  }

  const companyName = (company.company_name ?? '').trim();
  if (companyName) base.companyName = companyName;
  base.companyHeadline = formatCompanyCompactSenderLine(company);

  const directors = formatDirectorFooterLines(company);
  if (directors.length) base.directors = directors.join(', ');
  const registry = formatRegistryLine(company);
  if (registry) base.registry = registry;

  if ((company.iban ?? '').trim()) base.iban = company.iban.trim();
  if ((company.bic ?? '').trim()) base.bic = company.bic.trim();
  if ((company.bank_name ?? '').trim()) base.bankName = company.bank_name.trim();
  if ((company.phone ?? '').trim()) base.phone = company.phone.trim();
  if (companyMail) base.email = companyMail;
  if ((company.website ?? '').trim()) {
    base.website = company.website.replace(/^https?:\/\//i, '').trim() || company.website.trim();
  }
  const addressLines = formatCompanyAddressLines(company);
  base.companyStreet = addressLines[0] ?? '';
  base.companyCity = addressLines[1] ?? '';
  if ((company.vat_id ?? '').trim()) base.vatId = company.vat_id.trim();
  if ((company.tax_number ?? '').trim()) base.taxNumber = company.tax_number.trim();

  return base;
}

export function sampleTokenValues(sample: LayoutSampleData = LAYOUT_SAMPLE_DATA): LayoutTokenValues {
  return {
    leistungsort: sample.leistungsort,
    leistungszeitraum: sample.leistungszeitraum,
    leistungszeitraumLabel: leistungszeitraumFieldLabel(sample.documentType),
    issueDate: sample.issueDate,
    issueDateLabel: issueDateFieldLabel(sample.documentType),
    dueDate: sample.dueDate,
    customerNumber: sample.customerNumber,
    projectNumber: sample.projectNumber,
    orderNumber: sample.orderNumber,
    orderNumberLabel: DEFAULT_FIELD_LABELS.orderNumber,
    orderTotal: '12.500,00 €',
    invoiceNumber: sample.invoiceNumber,
    invoiceNumberLabel: documentNumberFieldLabel(sample.documentType),
    documentType: sample.documentType,
    documentTitle: sample.documentTitle,
    offerTitle: sample.offerTitleRest,
  };
}

export function sampleValueForLabeledField(
  key: LayoutElementKey,
  sample: LayoutSampleData = LAYOUT_SAMPLE_DATA,
): string {
  switch (key) {
    case 'documentTitle':
    case 'invoiceNumber':
      return sample.invoiceNumber;
    case 'issueDate':
      return sample.issueDate;
    case 'dueDate':
      return sample.dueDate;
    case 'customerNumber':
      return sample.customerNumber;
    case 'projectNumber':
      return sample.projectNumber;
    case 'orderNumber':
      return sample.orderNumber;
    case 'leistungsort':
      return sample.leistungsort;
    case 'leistungszeitraum':
      return sample.leistungszeitraum;
    default:
      return '';
  }
}

export function sampleContentForElement(
  el: LayoutElement,
  _preview = true,
  sample: LayoutSampleData = LAYOUT_SAMPLE_DATA,
  opts?: { hideOfferTitleTypePrefix?: boolean },
): { title?: string; titleBold?: boolean; lines: string[]; linesBold?: boolean } {
  switch (el.key) {
    case 'logo':
      return { lines: ['Firmenlogo'] };
    case 'headerBanner':
      return { lines: ['Bild'] };
    case 'signatureImage':
      return { lines: ['Unterschrift'] };
    case 'senderHeadline':
      return { lines: [sample.companyHeadline] };
    case 'documentType':
      return { lines: [sample.documentType] };
    case 'recipient':
      return {
        lines: [sample.recipientName, sample.recipientStreet, sample.recipientCity],
      };
    case 'ansprechpartner':
      return {
        title: 'Ihr Ansprechpartner',
        titleBold: true,
        lines: [sample.ansprechLine, sample.ansprechMail],
      };
    case 'offerTitle':
      return {
        lines: [
          formatOfferTitleLine(sample.documentType, sample.offerTitleRest, {
            hidePrefix: opts?.hideOfferTitleTypePrefix,
          }),
        ],
      };
    case 'documentTitle':
    case 'invoiceNumber':
    case 'issueDate':
    case 'dueDate':
    case 'customerNumber':
    case 'projectNumber':
    case 'orderNumber':
    case 'leistungsort':
    case 'leistungszeitraum': {
      const defaultLabel =
        el.key === 'documentTitle'
          ? sample.documentType
          : el.key === 'invoiceNumber'
            ? documentNumberFieldLabel(sample.documentType)
            : el.key === 'issueDate'
              ? issueDateFieldLabel(sample.documentType)
              : el.key === 'leistungszeitraum'
                ? leistungszeitraumFieldLabel(sample.documentType) || undefined
                : undefined;
      const pres = labeledFieldPresentation(
        el,
        sampleValueForLabeledField(el.key, sample),
        defaultLabel ? { defaultLabel } : undefined,
      );
      if (pres.placement === 'inline') {
        const sep = inlineLabelValueSeparator(el.key);
        return {
          lines: [pres.label ? `${pres.label}${sep}${pres.value}` : pres.value],
          linesBold: false,
        };
      }
      return {
        title: pres.label || undefined,
        lines: [pres.value],
        linesBold: true,
      };
    }
    case 'legalBlock':
      return {
        lines: [
          sample.directors,
          sample.registry,
          sample.vatId.trim() ? `USt-ID ${sample.vatId}` : '',
          sample.taxNumber.trim() ? `Steuernummer ${sample.taxNumber}` : '',
        ].filter((ln) => ln.trim().length > 0),
      };
    case 'bankBlock':
      return {
        lines: [
          sample.bankName,
          sample.iban.trim() ? `IBAN ${sample.iban}` : '',
          sample.bic.trim() ? `BIC ${sample.bic}` : '',
        ].filter((ln) => ln.trim().length > 0),
      };
    case 'contactBlock':
      return {
        lines: [
          sample.website,
          sample.phone.trim() ? `Telefon ${sample.phone}` : '',
          sample.email.trim() ? `E-Mail ${sample.email}` : '',
        ].filter((ln) => ln.trim().length > 0),
      };
    case 'companyAddress':
      return {
        lines: [sample.companyStreet, sample.companyCity].filter((ln) => ln.trim().length > 0),
      };
    case 'companyPhone':
      return { lines: sample.phone.trim() ? [sample.phone.trim()] : [] };
    case 'companyEmail':
      return { lines: sample.email.trim() ? [sample.email.trim()] : [] };
    case 'companyWebsite':
      return { lines: sample.website.trim() ? [sample.website.trim()] : [] };
    case 'pageNumber':
      return { lines: ['Seite 1 von 1'] };
    case 'freeText': {
      const raw = el.text?.trim() || 'Freitext';
      return { lines: [expandFreeTextTokens(raw, sampleTokenValues(sample))] };
    }
    case 'divider':
      return { lines: [] };
    case 'colorBlock':
      return { lines: [] };
    default:
      return { lines: [String((el as { key: LayoutElementKey }).key)] };
  }
}

export function updateElementInLayout(
  layout: DocumentLayoutV1,
  band: 'header' | 'footer',
  elementId: string,
  patch: Partial<LayoutElement>,
): DocumentLayoutV1 {
  const next = structuredClone(layout);
  const list = band === 'header' ? next.header.elements : next.footer.elements;
  const idx = list.findIndex((e) => e.id === elementId);
  if (idx < 0) return layout;
  list[idx] = { ...list[idx], ...patch };
  return next;
}

export function removeElementFromLayout(
  layout: DocumentLayoutV1,
  band: 'header' | 'footer',
  elementId: string,
): DocumentLayoutV1 {
  const next = structuredClone(layout);
  if (band === 'header') {
    next.header.elements = next.header.elements.filter((e) => e.id !== elementId);
  } else {
    next.footer.elements = next.footer.elements.filter((e) => e.id !== elementId);
  }
  return next;
}

export function findElementBand(
  layout: DocumentLayoutV1,
  elementId: string,
): 'header' | 'footer' | null {
  if (layout.header.elements.some((e) => e.id === elementId)) return 'header';
  if (layout.footer.elements.some((e) => e.id === elementId)) return 'footer';
  return null;
}

/**
 * Equal gap distribution between selected elements along one axis.
 * First/last stay put (outer edges); middle items get even spacing.
 * Requires ≥ 3 unlocked elements in the same band.
 */
export function distributeElementsInBand(
  elements: LayoutElement[],
  ids: string[],
  axis: 'horizontal' | 'vertical',
): LayoutElement[] {
  const selected = elements.filter((e) => ids.includes(e.id) && !e.locked);
  if (selected.length < 3) return elements;

  if (axis === 'horizontal') {
    const sorted = [...selected].sort((a, b) => a.xPt - b.xPt || a.yPt - b.yPt);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const spanStart = first.xPt;
    const spanEnd = last.xPt + last.wPt;
    const totalW = sorted.reduce((sum, e) => sum + e.wPt, 0);
    const free = spanEnd - spanStart - totalW;
    const gap = free / (sorted.length - 1);
    let cursor = spanStart;
    const nextX = new Map<string, number>();
    for (const el of sorted) {
      nextX.set(el.id, cursor);
      cursor += el.wPt + gap;
    }
    return elements.map((e) => (nextX.has(e.id) ? { ...e, xPt: nextX.get(e.id)! } : e));
  }

  const sorted = [...selected].sort((a, b) => a.yPt - b.yPt || a.xPt - b.xPt);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const spanStart = first.yPt;
  const spanEnd = last.yPt + last.hPt;
  const totalH = sorted.reduce((sum, e) => sum + e.hPt, 0);
  const free = spanEnd - spanStart - totalH;
  const gap = free / (sorted.length - 1);
  let cursor = spanStart;
  const nextY = new Map<string, number>();
  for (const el of sorted) {
    nextY.set(el.id, cursor);
    cursor += el.hPt + gap;
  }
  return elements.map((e) => (nextY.has(e.id) ? { ...e, yPt: nextY.get(e.id)! } : e));
}

export function distributeSelectionInLayout(
  layout: DocumentLayoutV1,
  ids: string[],
  axis: 'horizontal' | 'vertical',
): DocumentLayoutV1 {
  const next = structuredClone(layout);
  const headerIds = ids.filter((id) => next.header.elements.some((e) => e.id === id));
  const footerIds = ids.filter((id) => next.footer.elements.some((e) => e.id === id));
  if (headerIds.length >= 3) {
    next.header.elements = distributeElementsInBand(next.header.elements, headerIds, axis);
  }
  if (footerIds.length >= 3) {
    next.footer.elements = distributeElementsInBand(next.footer.elements, footerIds, axis);
  }
  return next;
}

function valueTokenForLabeledMerge(key: LayoutElementKey): string | null {
  if (key === 'documentTitle') return 'invoiceNumber';
  const tokens = [
    'leistungsort',
    'leistungszeitraum',
    'issueDate',
    'dueDate',
    'customerNumber',
    'projectNumber',
    'orderNumber',
    'invoiceNumber',
  ] as const;
  return (tokens as readonly string[]).includes(key) ? key : null;
}

/** Dynamic label token for merge — keeps freeText document-type-aware. */
function labelTokenForLabeledMerge(key: LayoutElementKey): string | null {
  if (key === 'documentTitle' || key === 'invoiceNumber') return 'invoiceNumberLabel';
  if (key === 'leistungszeitraum') return 'leistungszeitraumLabel';
  if (key === 'issueDate') return 'issueDateLabel';
  if (key === 'orderNumber') return 'orderNumberLabel';
  return null;
}

/** „0,5“ und „.5“ zählen als gültige Punkt-Eingabe; leer / unlesbar = kein Wert. */
export function parsePtValue(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Editor display for a pt value: max. 2 Nachkommastellen, Komma als Trenner. */
export function formatPtValue(n: number): string {
  return String(Number(n.toFixed(2))).replace('.', ',');
}

export const LAYOUT_IMAGE_KEYS = ['logo', 'headerBanner', 'signatureImage'] as const;

export function isLayoutImageKey(key: LayoutElementKey): boolean {
  return (LAYOUT_IMAGE_KEYS as readonly string[]).includes(key);
}

export type LayoutResizeCorner = 'ne' | 'nw' | 'se' | 'sw';

export function isLayoutResizeCorner(edge: string): edge is LayoutResizeCorner {
  return edge === 'ne' || edge === 'nw' || edge === 'se' || edge === 'sw';
}

/**
 * Corner-resize that keeps the original box aspect ratio (Google Slides + Shift).
 * The opposite corner stays anchored; scale is clamped to min size and band bounds.
 */
export function applyAspectLockedCornerResize(
  orig: { x: number; y: number; w: number; h: number },
  edge: LayoutResizeCorner,
  dx: number,
  dy: number,
  bounds: { minW: number; minH: number; maxX: number; maxY: number },
): { x: number; y: number; w: number; h: number } {
  const origW = Math.max(orig.w, 0.001);
  const origH = Math.max(orig.h, 0.001);
  const wFromPtr = edge.includes('e') ? origW + dx : origW - dx;
  const hFromPtr = edge.includes('s') ? origH + dy : origH - dy;
  const scaleW = wFromPtr / origW;
  const scaleH = hFromPtr / origH;
  let scale = Math.abs(scaleW - 1) >= Math.abs(scaleH - 1) ? scaleW : scaleH;
  scale = Math.max(scale, 0.001);

  const minScale = Math.max(bounds.minW / origW, bounds.minH / origH);
  const maxW = edge.includes('e') ? bounds.maxX - orig.x : orig.x + origW;
  const maxH = edge.includes('s') ? bounds.maxY - orig.y : orig.y + origH;
  const maxScale = Math.min(maxW / origW, maxH / origH);
  scale = Math.min(Math.max(scale, minScale), Math.max(minScale, maxScale));

  const w = origW * scale;
  const h = origH * scale;
  const x = edge.includes('w') ? orig.x + origW - w : orig.x;
  const y = edge.includes('n') ? orig.y + origH - h : orig.y;
  return { x, y, w, h };
}

/** Rough wrapped height for freeText given box width (Helvetica-ish ~0.5em/char). */
function expandFreeTextEstimateTokens(raw: string): string {
  return raw.replace(/\{\{(\w+)\}\}/g, (_, k: string) => {
    if (k === 'leistungsort') return 'Beispielweg 12, 12345 Musterstadt';
    if (k === 'leistungszeitraum' || k === 'issueDate' || k === 'dueDate') return '01.08.2026 – 08.08.2026';
    if (k === 'invoiceNumberLabel') return 'Rechnungsnr.';
    if (k === 'issueDateLabel') return 'Rechnungsdatum';
    if (k === 'leistungszeitraumLabel') return 'Leistungszeitraum';
    if (k === 'orderNumberLabel') return 'Bestellnummer';
    if (k === 'invoiceNumber' || k === 'projectNumber' || k === 'orderNumber' || k === 'customerNumber') {
      return 'XXXX-26-XXXXX';
    }
    return '————';
  });
}

export function estimateFreeTextHeightPt(text: string, wPt: number, fontSizePt: number): number {
  const raw = (text ?? '').trim() || ' ';
  const lineHeight = fontSizePt * 1.35;
  const avgCharW = Math.max(4, fontSizePt * 0.5);
  const charsPerLine = Math.max(12, Math.floor(Math.max(40, wPt) / avgCharW));
  const paragraphs = raw.split(/\r?\n/);
  let lines = 0;
  for (const para of paragraphs) {
    const len = expandFreeTextEstimateTokens(para).length;
    lines += Math.max(1, Math.ceil(len / charsPerLine));
  }
  return Math.max(Math.ceil(lineHeight), Math.ceil(lines * lineHeight));
}

/**
 * Merge ≥2 labeled fields in one band into a single freeText line:
 * `Label: {{token}} • Label: {{token}}`
 * Returns null if the selection cannot be merged.
 */
export function mergeLabeledSelectionToFreeText(
  layout: DocumentLayoutV1,
  ids: string[],
): { layout: DocumentLayoutV1; newId: string } | null {
  if (ids.length < 2) return null;

  type Found = { el: LayoutElement; band: 'header' | 'footer' };
  const found: Found[] = [];
  for (const id of ids) {
    const band = findElementBand(layout, id);
    if (!band) return null;
    const el = (band === 'header' ? layout.header.elements : layout.footer.elements).find(
      (e) => e.id === id,
    );
    if (!el || el.locked) return null;
    if (!isLabeledFieldKey(el.key)) return null;
    if (!valueTokenForLabeledMerge(el.key)) return null;
    found.push({ el, band });
  }

  const band = found[0].band;
  if (found.some((f) => f.band !== band)) return null;

  const sorted = [...found].sort(
    (a, b) => a.el.xPt - b.el.xPt || a.el.yPt - b.el.yPt,
  );

  const parts = sorted.map(({ el }) => {
    const token = valueTokenForLabeledMerge(el.key)!;
    const labelToken = labelTokenForLabeledMerge(el.key);
    // Prefer dynamic label tokens for document-type-aware fields; fall back to
    // baked-in labelText for fields with a static custom override.
    if (labelToken && el.labelText == null) {
      return `{{${labelToken}}}: {{${token}}}`;
    }
    const label = resolveFieldLabel(el);
    return label ? `${label}: {{${token}}}` : `{{${token}}}`;
  });
  const text = parts.join('  •  ');

  const minX = Math.min(...sorted.map((f) => f.el.xPt));
  const minY = Math.min(...sorted.map((f) => f.el.yPt));
  const maxR = Math.max(...sorted.map((f) => f.el.xPt + f.el.wPt));
  const first = sorted[0].el;
  const wPt = Math.max(80, maxR - minX);
  const fontSizePt = first.fontSizePt ?? 9.5;
  // One-line meta merge — size to text, not the tall union of stacked label boxes.
  const hPt = estimateFreeTextHeightPt(text, wPt, fontSizePt);
  const newId = newLayoutElementId('freeText');
  const freeTextEl: LayoutElement = {
    id: newId,
    key: 'freeText',
    xPt: minX,
    yPt: minY,
    wPt,
    hPt,
    align: first.align ?? 'left',
    bold: false,
    fontSizePt,
    color: first.color || '#334155',
    text,
    locked: false,
    z: Math.max(0, ...sorted.map((f) => f.el.z ?? 0)) + 1,
  };

  // Insert freeText first so mandatory coverage via tokens allows removal.
  let next = structuredClone(layout);
  if (band === 'header') next.header.elements.push(freeTextEl);
  else next.footer.elements.push(freeTextEl);

  for (const { el } of sorted) {
    if (!canRemoveElement(next, band, el.id)) {
      // Roll back — cannot safely remove a Pflichtfeld.
      return null;
    }
    next = removeElementFromLayout(next, band, el.id);
  }

  return { layout: next, newId };
}

/** Matches documentLayoutValidation band height limits. */
export const HEADER_HEIGHT_MIN_PT = 40;
export const HEADER_HEIGHT_MAX_PT = PDF_A4_HEIGHT_PT - 120;
export const FOOTER_HEIGHT_MIN_PT = 24;
export const FOOTER_HEIGHT_MAX_PT = 200;
const BAND_CONTENT_PAD_PT = 8;

function contentBottomPt(elements: LayoutElement[]): number {
  if (elements.length === 0) return 0;
  return Math.max(...elements.map((e) => e.yPt + e.hPt));
}

/**
 * Shrink (or grow) header/footer to the last element's bottom + padding.
 * Leftover band height after moving fields up otherwise stays as dead space
 * on the canvas and in the real PDF spacer.
 */
export function fitLayoutBandsToContent(layout: DocumentLayoutV1): DocumentLayoutV1 {
  let next = structuredClone(layout);
  next = updateBandHeight(
    next,
    'header',
    contentBottomPt(next.header.elements) + BAND_CONTENT_PAD_PT,
  );
  next = updateBandHeight(
    next,
    'footer',
    contentBottomPt(next.footer.elements) + BAND_CONTENT_PAD_PT,
  );
  return next;
}

export function bandHeightLimits(
  layout: DocumentLayoutV1,
  band: LayoutBand,
): { min: number; max: number } {
  if (band === 'header') {
    const contentMin = Math.max(
      HEADER_HEIGHT_MIN_PT,
      contentBottomPt(layout.header.elements) + BAND_CONTENT_PAD_PT,
    );
    const maxByBody = PDF_A4_HEIGHT_PT - layout.footer.heightPt - 80;
    return {
      min: contentMin,
      max: Math.max(contentMin, Math.min(HEADER_HEIGHT_MAX_PT, maxByBody)),
    };
  }
  const contentMin = Math.max(
    FOOTER_HEIGHT_MIN_PT,
    contentBottomPt(layout.footer.elements) + BAND_CONTENT_PAD_PT,
  );
  const maxByBody = PDF_A4_HEIGHT_PT - layout.header.heightPt - 80;
  return {
    min: contentMin,
    max: Math.max(contentMin, Math.min(FOOTER_HEIGHT_MAX_PT, maxByBody)),
  };
}

export function clampBandHeight(
  layout: DocumentLayoutV1,
  band: LayoutBand,
  heightPt: number,
): number {
  const { min, max } = bandHeightLimits(layout, band);
  if (!Number.isFinite(heightPt)) return min;
  return Math.min(max, Math.max(min, Math.round(heightPt)));
}

export function updateBandHeight(
  layout: DocumentLayoutV1,
  band: LayoutBand,
  heightPt: number,
): DocumentLayoutV1 {
  const next = structuredClone(layout);
  const clamped = clampBandHeight(next, band, heightPt);
  if (band === 'header') next.header.heightPt = clamped;
  else next.footer.heightPt = clamped;
  return next;
}

/**
 * Rounds to a whole point without pushing a box out of bounds that still fit
 * before rounding (drafts may already be out of bounds — those stay untouched).
 */
function snapWithinPt(value: number, sizePt: number, limitPt: number): number {
  const rounded = Math.max(0, Math.round(value));
  const fitBefore = value + sizePt <= limitPt + 0.5;
  if (fitBefore && rounded + sizePt > limitPt + 0.5) {
    return Math.max(0, Math.floor(limitPt - sizePt));
  }
  return rounded;
}

/** Line thickness stays on quarter points so hairlines survive a commit. */
export function snapLineThicknessPt(value: number): number {
  if (!Number.isFinite(value)) return LAYOUT_LINE_MIN_THICKNESS_PT;
  const snapped = Number((Math.round(value * 4) / 4).toFixed(2));
  return Math.max(LAYOUT_LINE_MIN_THICKNESS_PT, snapped);
}

export function roundElementGeometry(el: LayoutElement, bandHeightPt: number): LayoutElement {
  const verticalLine = el.key === 'divider' && resolveLineOrientation(el) === 'vertical';
  const horizontalLine = el.key === 'divider' && !verticalLine;
  const wPt = verticalLine ? snapLineThicknessPt(el.wPt) : Math.max(2, Math.round(el.wPt));
  const hPt = horizontalLine ? snapLineThicknessPt(el.hPt) : Math.max(1, Math.round(el.hPt));
  return {
    ...el,
    wPt,
    hPt,
    xPt: snapWithinPt(el.xPt, wPt, PDF_A4_WIDTH_PT),
    yPt: snapWithinPt(el.yPt, hPt, bandHeightPt),
  };
}

/**
 * Element boxes are absolute PDF coordinates, so keep every value on whole
 * points — otherwise editor and export drift apart by fractions.
 */
export function roundLayoutGeometry(layout: DocumentLayoutV1): DocumentLayoutV1 {
  const headerHeightPt = Math.min(
    Math.floor(HEADER_HEIGHT_MAX_PT),
    Math.max(HEADER_HEIGHT_MIN_PT, Math.ceil(layout.header.heightPt)),
  );
  const footerHeightPt = Math.min(
    FOOTER_HEIGHT_MAX_PT,
    Math.max(FOOTER_HEIGHT_MIN_PT, Math.ceil(layout.footer.heightPt)),
  );
  return {
    ...layout,
    header: {
      heightPt: headerHeightPt,
      elements: layout.header.elements.map((e) => roundElementGeometry(e, headerHeightPt)),
    },
    footer: {
      heightPt: footerHeightPt,
      elements: layout.footer.elements.map((e) => roundElementGeometry(e, footerHeightPt)),
    },
  };
}

/** Content-only minimum height (independent of current box size — used for resize mins). */
export function intrinsicContentHeightPt(el: LayoutElement): number {
  const fs = el.fontSizePt ?? 9.5;
  /** Match canvas / PDF band line-height (1.35), not a padded editor box. */
  const linesFor = (n: number, lineHeight = 1.35) => Math.ceil(n * fs * lineHeight);

  switch (el.key) {
    case 'logo':
    case 'headerBanner':
    case 'signatureImage':
      return 24;
    case 'divider':
      return Math.max(LAYOUT_LINE_MIN_THICKNESS_PT, el.hPt);
    case 'colorBlock':
      return 16;
    case 'senderHeadline':
    case 'offerTitle':
    case 'documentType':
    case 'pageNumber':
      return linesFor(1);
    case 'recipient':
      // 3 address lines at the tighter multiline rhythm (+ 2pt gaps).
      return linesFor(3, 1.25) + 4;
    case 'ansprechpartner':
      return linesFor(1) + 2 + linesFor(2, 1.25) + 2;
    case 'documentTitle':
    case 'invoiceNumber':
    case 'issueDate':
    case 'dueDate':
    case 'customerNumber':
    case 'projectNumber':
    case 'orderNumber':
    case 'leistungsort':
    case 'leistungszeitraum':
      return resolveLabelPlacement(el) === 'inline' ? linesFor(1) : linesFor(2) + 2;
    case 'legalBlock':
    case 'bankBlock':
    case 'contactBlock':
      return linesFor(4, 1.25) + 6;
    case 'companyAddress':
      return linesFor(2, 1.25) + 2;
    case 'companyPhone':
    case 'companyEmail':
    case 'companyWebsite':
      return linesFor(1);
    case 'freeText': {
      const text = (el.text ?? 'Freitext').trim() || 'Freitext';
      return estimateFreeTextHeightPt(text, el.wPt, fs);
    }
    default:
      return 14;
  }
}

/** Estimated content height so editor/PDF boxes are never shorter than their text. */
export function minContentHeightPt(el: LayoutElement): number {
  if (['logo', 'headerBanner', 'signatureImage', 'divider', 'colorBlock'].includes(el.key)) {
    return el.hPt;
  }
  return Math.max(el.hPt, intrinsicContentHeightPt(el));
}

/** Place recipient on DIN 5008 Form B window and grow header if needed. */
export function snapRecipientToDinAddressWindow(
  layout: DocumentLayoutV1,
  elementId: string,
): DocumentLayoutV1 {
  const win = DIN5008_FORM_B_ADDRESS_WINDOW;
  let next = updateElementInLayout(layout, 'header', elementId, {
    xPt: win.xPt,
    yPt: win.yPt,
    wPt: win.wPt,
    hPt: win.hPt,
  });
  const needBottom = win.yPt + win.hPt + BAND_CONTENT_PAD_PT;
  if (next.header.heightPt < needBottom) {
    next = updateBandHeight(next, 'header', needBottom);
  }
  return next;
}

/** Split legacy combined documentTitle (Belegzeile + Betreff) into two elements. */
export function ensureSplitDocumentTitle(layout: DocumentLayoutV1): DocumentLayoutV1 {
  const next = structuredClone(layout);
  if (next.header.elements.some((e) => e.key === 'offerTitle')) return next;
  const title = next.header.elements.find((e) => e.key === 'documentTitle');
  if (!title) return next;

  const gap = 4;
  const origH = title.hPt;
  if (origH >= 32) {
    const firstH = Math.max(16, Math.round(origH / 2) - gap / 2);
    const secondH = Math.max(16, origH - firstH - gap);
    title.hPt = firstH;
    next.header.elements.push({
      id: newLayoutElementId('offerTitle'),
      key: 'offerTitle',
      xPt: title.xPt,
      yPt: title.yPt + firstH + gap,
      wPt: title.wPt,
      hPt: secondH,
      align: title.align,
      bold: title.bold,
      italic: title.italic,
      underline: title.underline,
      fontSizePt: title.fontSizePt,
      color: title.color,
      locked: false,
      z: title.z,
    });
  } else {
    const secondH = 20;
    const insertAt = title.yPt + title.hPt;
    for (const e of next.header.elements) {
      if (e.id === title.id) continue;
      if (e.yPt >= insertAt - 0.5) e.yPt += secondH + gap;
    }
    next.header.elements.push({
      id: newLayoutElementId('offerTitle'),
      key: 'offerTitle',
      xPt: title.xPt,
      yPt: insertAt + gap,
      wPt: title.wPt,
      hPt: secondH,
      align: title.align,
      bold: title.bold,
      italic: title.italic,
      underline: title.underline,
      fontSizePt: title.fontSizePt,
      color: title.color,
      locked: false,
      z: title.z,
    });
    next.header.heightPt += secondH + gap;
  }
  return next;
}

const LAYOUT_FIXED_SIZE_KEYS = new Set([
  'logo',
  'headerBanner',
  'signatureImage',
  'divider',
  'colorBlock',
]);

/** Address / footer blocks vary at runtime — grow if clipped, never collapse. */
const LAYOUT_GROW_ONLY_KEYS = new Set([
  'recipient',
  'ansprechpartner',
  'legalBlock',
  'bankBlock',
  'contactBlock',
  'companyAddress',
]);

function xRangesOverlap(a: LayoutElement, b: LayoutElement): boolean {
  return a.xPt < b.xPt + b.wPt - 0.5 && b.xPt < a.xPt + a.wPt - 0.5;
}

/** Grow/shrink text boxes to their content and shift following siblings. */
export function expandLayoutToFitContent(layout: DocumentLayoutV1): DocumentLayoutV1 {
  const next = ensureSplitDocumentTitle(layout);

  const expandBand = (elements: LayoutElement[], bandHeight: number): number => {
    const sorted = [...elements].sort((a, b) => a.yPt - b.yPt || a.xPt - b.xPt);
    let height = bandHeight;
    for (let i = 0; i < sorted.length; i++) {
      const el = sorted[i];
      if (LAYOUT_FIXED_SIZE_KEYS.has(el.key)) continue;
      const oldH = el.hPt;
      const intrinsic = intrinsicContentHeightPt(el);
      const needed = LAYOUT_GROW_ONLY_KEYS.has(el.key) ? Math.max(oldH, intrinsic) : intrinsic;
      if (Math.abs(needed - oldH) <= 0.5) continue;
      const delta = needed - oldH;
      el.hPt = needed;
      for (let j = 0; j < sorted.length; j++) {
        if (j === i) continue;
        // Only boxes that sat fully below this one in the same column.
        if (sorted[j].yPt + 0.5 < el.yPt + oldH) continue;
        if (!xRangesOverlap(el, sorted[j])) continue;
        sorted[j].yPt += delta;
      }
      height += delta;
    }
    return height;
  };

  next.header.heightPt = clampBandHeight(
    next,
    'header',
    expandBand(next.header.elements, next.header.heightPt),
  );
  next.footer.heightPt = clampBandHeight(
    next,
    'footer',
    expandBand(next.footer.elements, next.footer.heightPt),
  );
  return fitLayoutBandsToContent(next);
}
